import * as React from 'react';

import type { CampaignDetailDto, CycleNumberDto } from '@/api/campaign-client';
import { useAddCycle, useRemoveCycle, useSetCampaignDates } from '@/api/use-campaigns';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { StatusDot } from '@/components/ui/panel';
import { cycleNumberLabel, cycleStateLabel } from '@/lib/campaign-labels';
import { formatParisInput } from '@/lib/paris-time';
import { cn } from '@/lib/utils';
import {
  addCycle2,
  applyManualEdit,
  buildSavePlan,
  calendarAlerts,
  cancelCycle2,
  contextFromCampaign,
  dirtyLines,
  effectiveValues,
  EMPTY_DRAFT,
  hasCycle2,
  isCampaignOpened,
  isFieldEditable,
  isoFromCampaign,
  lineFields,
  pruneSynced,
  valuesFromCampaign,
  type CalendarDraft,
  type CalendarField,
  type CalendarLine,
} from '@/routes/admin/campaigns/calendar-model';
import { DARK_BUTTON, SideCard, TEXT_BUTTON } from '@/routes/admin/campaigns/detail-ui';

// =============================================================================
// calendar-panel.tsx — Carte « Calendrier » du détail campagne : dates de campagne + cycles 1 et 2.
// =============================================================================
// Fusionne les anciennes sections « Dates de démarrage et de fin » (V-CREATE, DBU §2.5 l.1125, F0-d) et
// « Planning des cycles » (planifier / modifier / supprimer un cycle Pendante, prolonger un cycle Ouverte,
// cycle clôturé non modifiable — GATE_HUMAN 2026-09-09 et 2026-09-14). Un seul brouillon, un seul
// « Enregistrer » qui n'envoie QUE les lignes modifiées (logique pure : calendar-model.ts).
//
// GATE_HUMAN 2026-09-16 : défauts modifiables et propagation (début campagne ⇒ début C1 ; fin campagne ⇒ fin
// C2, ou fin C1 sans C2 ; fin C1 ⇒ début C2), une fin de cycle postérieure repousse la fin de campagne,
// campagne ouverte ⇒ seule la fin de campagne est modifiable ; saisie ET affichage en heure de Paris.
// Mutations existantes uniquement : useSetCampaignDates, useAddCycle (upsert), useRemoveCycle.
//
// WCAG 2.1 AA : chaque champ porte un libellé complet (préfixe visuel + suffixe sr-only), alerte d'incohérence
// en TEXTE reliée au champ (aria-describedby, le fond ambre n'est qu'un renfort), erreurs par ligne role=alert.

type LineErrors = Readonly<Partial<Record<string, string>>>;

export function CalendarPanel({
  campaign,
}: {
  readonly campaign: CampaignDetailDto;
}): React.JSX.Element {
  const setDates = useSetCampaignDates();
  const addCycle = useAddCycle();
  const removeCycle = useRemoveCycle();

  const initial = valuesFromCampaign(campaign);
  const originalIso = isoFromCampaign(campaign);
  const ctx = contextFromCampaign(campaign);

  const [draft, setDraft] = React.useState<CalendarDraft>(EMPTY_DRAFT);
  // Relecture serveur (après enregistrement ou autre mutation) : on retire du brouillon ce qui est désormais
  // persisté, en conservant les saisies encore différentes (ligne en erreur, saisie en cours).
  const serverKey = JSON.stringify([initial, ctx]);
  const [syncedKey, setSyncedKey] = React.useState(serverKey);
  if (syncedKey !== serverKey) {
    setSyncedKey(serverKey);
    setDraft((d) => pruneSynced(initial, ctx, d));
  }

  const [saving, setSaving] = React.useState(false);
  const [errors, setErrors] = React.useState<LineErrors>({});
  const [deleting, setDeleting] = React.useState<CycleNumberDto | null>(null);

  const values = effectiveValues(initial, ctx, draft);
  const alerts = calendarAlerts(values);
  const dirty = dirtyLines(initial, ctx, draft);
  const cycle2Present = hasCycle2(ctx, draft);

  const onEdit = (field: CalendarField, value: string): void => {
    setDraft((d) => applyManualEdit(initial, ctx, d, field, value));
  };

  const onSave = async (): Promise<void> => {
    const plan = buildSavePlan(initial, originalIso, ctx, draft);
    setSaving(true);
    setErrors({});
    const next: Record<string, string> = {};
    for (const step of plan) {
      try {
        if (step.kind === 'dates') {
          await setDates.mutateAsync({
            campaignId: campaign.campaignId,
            body: { startDate: step.startDate, endDate: step.endDate },
          });
        } else if (step.kind === 'cycle') {
          await addCycle.mutateAsync({
            campaignId: campaign.campaignId,
            body: {
              cycleNumber: step.cycleNumber,
              openAt: step.openAt,
              closeAt: step.closeAt,
            },
          });
        } else {
          next[String(step.line)] = step.message;
        }
      } catch (error) {
        next[String(step.line)] =
          error instanceof Error ? error.message : 'Enregistrement impossible.';
      }
    }
    setErrors(next);
    setSaving(false);
  };

  const onConfirmDelete = (): void => {
    if (deleting === null) {
      return;
    }
    removeCycle.mutate(
      { campaignId: campaign.campaignId, cycleNumber: deleting },
      {
        onSuccess: () => {
          if (deleting === 2) {
            setDraft((d) => cancelCycle2(d));
          }
          setDeleting(null);
        },
      },
    );
  };

  const opened = isCampaignOpened(campaign.state);
  const terminal = campaign.state === 'Publiee' || campaign.state === 'Abandonnee';

  const fieldProps = (field: CalendarField) => ({
    field,
    value: values[field],
    editable: isFieldEditable(field, ctx, draft),
    alert: alerts[field],
    onEdit,
  });

  return (
    <SideCard
      headingId="detail-calendar-heading"
      label="Calendrier"
      actions={
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={DARK_BUTTON}
          disabled={dirty.length === 0 || saving}
          onClick={() => {
            void onSave();
          }}
        >
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      }
    >
      <CalendarBlock
        line="campaign"
        title="Campagne"
        status={
          opened && !terminal ? (
            <span className="inline-flex items-center gap-1.5">
              <StatusDot className="bg-success" />
              En cours
            </span>
          ) : undefined
        }
        alerts={alerts}
        error={errors.campaign}
      >
        <DateField {...fieldProps('campaignStart')} shortLabel="Début" srSuffix="de la campagne" />
        <DateField {...fieldProps('campaignEnd')} shortLabel="Fin" srSuffix="de la campagne" />
      </CalendarBlock>

      {([1, 2] as const).map((n) => {
        if (n === 2 && !cycle2Present) {
          return (
            <div key={n} className="border-t border-border pt-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDraft((d) => addCycle2(initial, ctx, d))}
              >
                Ajouter le cycle 2
              </Button>
            </div>
          );
        }
        const state = n === 1 ? ctx.cycle1 : ctx.cycle2;
        const label = cycleNumberLabel(n);
        const [openField, closeField] = lineFields(n) as readonly [CalendarField, CalendarField];
        const frozen = state === 'Cloturee' || state === 'Verrouillee';
        return (
          <CalendarBlock
            key={n}
            line={n}
            title={label}
            status={
              state === null ? (n === 1 ? 'Non planifié' : 'Nouveau') : cycleStateLabel(state).label
            }
            alerts={alerts}
            error={errors[String(n)]}
            actions={
              state === 'Pendante' ? (
                <button
                  type="button"
                  className={TEXT_BUTTON}
                  aria-label={`Supprimer le ${label}`}
                  onClick={() => setDeleting(n)}
                >
                  Supprimer
                </button>
              ) : state === null && n === 2 ? (
                <button
                  type="button"
                  className={TEXT_BUTTON}
                  onClick={() => setDraft((d) => cancelCycle2(d))}
                >
                  Annuler l&apos;ajout
                </button>
              ) : undefined
            }
            note={
              state === null && n === 1 && campaign.cycles.length === 0 ? (
                <p role="status" className="text-xs text-muted-foreground">
                  Aucun cycle planifié.
                </p>
              ) : frozen ? (
                <p className="text-xs text-muted-foreground">Cycle clôturé : non modifiable</p>
              ) : state === 'Ouverte' ? (
                <p className="text-xs text-muted-foreground">
                  Cycle ouvert : prolongation seule (fermeture repoussée, ouverture figée).
                </p>
              ) : undefined
            }
          >
            <DateField {...fieldProps(openField)} shortLabel="Début" srSuffix={`du ${label}`} />
            <DateField {...fieldProps(closeField)} shortLabel="Fin" srSuffix={`du ${label}`} />
          </CalendarBlock>
        );
      })}

      {removeCycle.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {removeCycle.error.message}
        </p>
      ) : null}

      <p className="border-t border-border pt-3 font-mono text-xs text-muted-foreground">
        Heure de Paris · 2 cycles max.
      </p>

      <ConfirmDialog
        open={deleting !== null}
        title={deleting === null ? 'Supprimer le cycle ?' : `Supprimer le ${cycleNumberLabel(deleting)} ?`}
        description="Le cycle planifié sera définitivement retiré du planning. Cette action corrige une saisie erronée avant le lancement ; vous pourrez le re-planifier ensuite. Confirmez-vous la suppression ?"
        confirmLabel={removeCycle.isPending ? 'Suppression…' : 'Confirmer la suppression'}
        confirmDisabled={removeCycle.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </SideCard>
  );
}

function alertId(line: CalendarLine): string {
  return `calendar-alert-${String(line)}`;
}

function CalendarBlock({
  line,
  title,
  status,
  alerts,
  error,
  actions,
  note,
  children,
}: {
  readonly line: CalendarLine;
  readonly title: string;
  readonly status?: React.ReactNode;
  readonly alerts: Readonly<Partial<Record<CalendarField, string>>>;
  readonly error: string | undefined;
  readonly actions?: React.ReactNode;
  readonly note?: React.ReactNode;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const blockAlert = lineFields(line)
    .map((f) => alerts[f])
    .find((a) => a !== undefined);
  return (
    <div
      role="group"
      aria-labelledby={`calendar-block-${String(line)}`}
      className={cn('space-y-2', line === 'campaign' ? undefined : 'border-t border-border pt-4')}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 id={`calendar-block-${String(line)}`} className="text-sm font-medium">
          {title}
        </h3>
        <span className="flex items-center gap-3 text-xs">
          {blockAlert !== undefined ? (
            <span
              id={alertId(line)}
              className="rounded-sm bg-dash-critical-bg px-1.5 py-0.5 font-medium text-dash-critical-fg"
            >
              {blockAlert}
            </span>
          ) : status !== undefined ? (
            <span className="text-muted-foreground">{status}</span>
          ) : null}
          {actions}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">{children}</div>
      {note}
      {error !== undefined ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function lineOf(field: CalendarField): CalendarLine {
  if (field.startsWith('campaign')) {
    return 'campaign';
  }
  return field.startsWith('c1') ? 1 : 2;
}

function DateField({
  field,
  value,
  editable,
  alert,
  onEdit,
  shortLabel,
  srSuffix,
}: {
  readonly field: CalendarField;
  readonly value: string;
  readonly editable: boolean;
  readonly alert: string | undefined;
  readonly onEdit: (field: CalendarField, value: string) => void;
  readonly shortLabel: string;
  readonly srSuffix: string;
}): React.JSX.Element {
  const id = `calendar-${field}`;
  if (!editable) {
    return (
      <div className="space-y-1">
        <span aria-hidden="true" className="block text-xs text-muted-foreground">
          {shortLabel}
        </span>
        <p className="flex h-9 items-center font-mono text-xs text-muted-foreground">
          <span className="sr-only">
            {shortLabel} {srSuffix} (non modifiable) :{' '}
          </span>
          {formatParisInput(value)}
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs text-muted-foreground">
        {shortLabel}
        <span className="sr-only"> {srSuffix}</span>
      </label>
      <Input
        id={id}
        type="datetime-local"
        value={value}
        onChange={(e) => onEdit(field, e.target.value)}
        aria-describedby={alert !== undefined ? alertId(lineOf(field)) : undefined}
        className={cn(
          'h-9 px-2 font-mono text-xs',
          alert !== undefined ? 'bg-dash-critical-bg' : undefined,
        )}
      />
    </div>
  );
}
